import * as React from 'react';
import { Badge, Checkbox, Chip, IconButton, TableCell, TableRow, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import DoNotStripSpacesSpan from '../common/DoNotStripSpacesSpan';
import CertifiedBadge from '../common/CertifiedBadge';
import DownloadButton from './download/DownloadButton';
import ExtractionBatchActions from './ExtractionBatchActions';
import ExtractionBatchRowGroup from './ExtractionBatchRowGroup';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { IExtractionBatch, ExtractionStatus, DataProcessingStatus } from '../../types/my-types';
import { Link } from 'react-router';
import { format } from 'date-fns';

import { AppContext } from '../../App';
import useSearchParams from '../hooks/useSearchParams';
import OverflowTooltip from '../common/OverflowTooltip';
import { LinkTableStyled } from '../common/StyledComponents';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CircularProgress from '@mui/material/CircularProgress';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DoneIcon from '@mui/icons-material/Done';

export interface ExtractionBatchRowProps {
  eb: IExtractionBatch;
}

export const renderStatusIcon = (
  label: string,
  ebStatus: ExtractionStatus,
  dataProcessingStatus: DataProcessingStatus,
  hasValidationErrors?: boolean
) => {
  let useIcon: React.ReactNode = null;

  if (ebStatus === 'Success' && dataProcessingStatus === 'Success') {
    useIcon = (
      <CheckCircleIcon
        style={{ color: '#4CAF50', height: '17px' }}
        data-cy={`icon-status-eb-${ebStatus}-${label}`}
      />
    );
  } else if (
    ebStatus === 'In Progress' ||
    ebStatus === 'Pending' ||
    dataProcessingStatus === 'In Progress' ||
    dataProcessingStatus === 'Pending'
  ) {
    useIcon = (
      <CircularProgress
        size={15}
        style={{ marginLeft: '5px' }}
        data-cy={`icon-status-eb-${ebStatus}-${label}`}
      />
    );
  } else if (ebStatus === 'Partial Success' || dataProcessingStatus === 'Partial Success') {
    useIcon = (
      <ReportProblemIcon
        style={{ color: '#FFC107', height: '17px' }}
        data-cy={`icon-status-eb-${ebStatus}-${label}`}
      />
    );
  } else {
    useIcon = (
      <ErrorIcon
        style={{ color: '#F44336', height: '17px' }}
        data-cy={`icon-status-eb-${ebStatus}-${label}`}
      />
    );
  }

  const iconWithBadge = hasValidationErrors ? (
    <Badge
      variant="dot"
      color="error"
      overlap="circular"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{ marginRight: '5px' }}
    >
      {useIcon}
    </Badge>
  ) : (
    <span style={{ marginRight: '5px', display: 'inline-flex', alignItems: 'center' }}>
      {useIcon}
    </span>
  );

  return (
    <Tooltip
      title={`Extraction: ${ebStatus || ''} / Transformation: ${dataProcessingStatus || ''}`}
      placement="top"
    >
      {iconWithBadge}
    </Tooltip>
  );
};

const ExtractionBatchRow: React.FC<ExtractionBatchRowProps> = ({ eb }) => {
  const { dataPoolId } = React.useContext(AppContext);

  const [openRow, setOpenRow] = React.useState(false);
  const [loadingSendExtractionBatch, setLoadingSendExtractionBatch] = React.useState(false);

  const { setSearchParam } = useSearchParams();

  const { search } = useLocation();
  const navigate = useNavigate();
  const selectedIds = new URLSearchParams(search).get('selectedIds')?.split(',') || [];
  const selected = selectedIds.includes(eb.id);

  const handleToggleSelected = () => {
    const params = new URLSearchParams(search);
    if (selected) {
      const remaining = selectedIds.filter((id) => id !== eb.id);
      if (remaining.length === 0) {
        params.delete('selectedIds');
      } else {
        params.set('selectedIds', remaining.join(','));
      }
    } else {
      params.set('selectedIds', [...selectedIds, eb.id].join(','));
    }
    navigate({ search: params.toString() });
  };

  const displayLinkToTrainingSet = (
    trainingSetUrl: string,
    trainingSetLabel: string,
    trainingSetId?: string,
    certifiedAt?: string | null,
    certifiedBy?: string | null
  ) => {
    if (!trainingSetId) return displayShortenedTrainingSetName(trainingSetLabel);

    const trainingSet = new URL(trainingSetUrl);
    const pathParts = trainingSet.pathname.split('/').filter((part) => part !== '');
    const dataPoolFromTrainingSet = pathParts[2];

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <OverflowTooltip title={trainingSetLabel} placement="bottom">
          <Link
            to={`/data-pools/${dataPoolFromTrainingSet}/train/${trainingSetId}/details`}
            data-cy={`lbl-training-set-name-${trainingSetLabel}`}
            style={{
              color: '#5f6d7e',
              fontSize: '12px',
              textDecoration: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px',
              display: 'inline-block',
            }}
          >
            {trainingSetLabel}
          </Link>
        </OverflowTooltip>
        <CertifiedBadge certifiedAt={certifiedAt} certifiedBy={certifiedBy} />
        <Tooltip title={'Filter'} placement="top">
          <FilterAltIcon
            style={{ color: '#5f6d7e', fontSize: '12px', marginLeft: '5px', cursor: 'pointer' }}
            onClick={() => setSearchParam('training_set_label', trainingSetLabel)}
          />
        </Tooltip>
      </div>
    );
  };

  const displayShortenedTrainingSetName = (trainingSetLabel: string) => {
    return (
      <DoNotStripSpacesSpan
        title={trainingSetLabel}
        cypressId={`lbl-training-set-name-${trainingSetLabel}`}
      />
    );
  };

  return (
    <>
      <TableRow data-cy={`tbl-row-extraction-batch-${eb.label}`}>
        <TableCell
          align="center"
          sx={{
            width: '40px',
            px: '4px !important',
            borderLeft: selected ? '4px solid #65BBA9' : '4px solid transparent',
          }}
          onClick={handleToggleSelected}
        >
          <Checkbox
            size="small"
            checked={selected}
            onChange={handleToggleSelected}
            inputProps={
              {
                'aria-label': 'select extraction batch',
                'data-cy': `checkbox-eb-row-${eb.label}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
            sx={{ color: '#65BBA9', '&.Mui-checked': { color: '#65BBA9' } }}
          />
        </TableCell>
        <TableCell component="th" scope="row">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }} data-cy={`eb-row-${eb.label}`}>
                {renderStatusIcon(
                  eb.label,
                  eb.status,
                  eb.data_processing_status,
                  eb.has_undismissed_validation_errors
                )}
                <OverflowTooltip title={eb.label || ''}>
                  <LinkTableStyled
                    to={`/data-pools/${dataPoolId}/extraction-batch/${eb.id}/details`}
                    data-cy={`lbl-extraction-batch-name-${eb.label}`}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '300px',
                      display: 'inline-block',
                    }}
                  >
                    {eb.label || 'No Label'}
                  </LinkTableStyled>
                </OverflowTooltip>
                {eb.is_experimental && (
                  <Chip
                    label="EXPERIMENTAL"
                    size="small"
                    sx={{
                      ml: 1,
                      height: '18px',
                      fontSize: '10px',
                      fontWeight: 600,
                      backgroundColor: '#e8f4fd',
                      color: '#1565c0',
                      border: '1px solid #90caf9',
                    }}
                    data-cy={`chip-experimental-${eb.label}`}
                  />
                )}
              </div>
              <div style={{ marginLeft: '29px', marginTop: '5px' }}>
                <span>
                  {eb.training_set_label ? (
                    displayLinkToTrainingSet(
                      eb.training_set,
                      eb.training_set_label,
                      eb.training_set_id,
                      eb.training_set_certified_at,
                      eb.training_set_certified_by
                    )
                  ) : (
                    <span style={{ color: '#5f6d7e', fontSize: '12px' }}>Auto-Classify</span>
                  )}
                </span>
              </div>
            </div>
            <div>
              {!eb.training_set && (eb.status === 'Success' || eb.status === 'Partial Success') && (
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => setOpenRow(!openRow)}
                  data-cy="btn-auto-classify-caret"
                >
                  {openRow ? <KeyboardArrowDownIcon /> : <KeyboardArrowRight />}
                </IconButton>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell align="center" sx={{ color: '#5f6d7e' }}>
          {eb.finished ? Number(eb.extractions) : Number(eb.documents)}
        </TableCell>
        <TableCell align="center" sx={{ color: '#5f6d7e' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title={format(new Date(eb.created_at), 'yyyy-MM-dd HH:mm:ss')} placement="top">
              <span data-cy={`lbl-extraction-batch-created-${eb.label}`}>
                {format(new Date(eb.created_at), 'yyyy-MM-dd')}
              </span>
            </Tooltip>
            <Tooltip title={'Filter'} placement="top">
              <FilterAltIcon
                style={{ color: '#5f6d7e', fontSize: '12px', marginLeft: '5px', cursor: 'pointer' }}
                onClick={() =>
                  setSearchParam('created_at', format(new Date(eb.created_at), 'yyyy-MM-dd'))
                }
              />
            </Tooltip>
          </div>
        </TableCell>
        <TableCell align="center" sx={{ color: '#5f6d7e' }}>
          <Tooltip
            title={`Updated: ${format(new Date(eb.updated_at), 'yyyy-MM-dd HH:mm:ss')}`}
            placement="top"
          >
            <span>{format(new Date(eb.updated_at), 'yyyy-MM-dd')}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          {loadingSendExtractionBatch === true ? (
            <CircularProgress size={15} style={{ marginLeft: '5px', marginRight: '5px' }} />
          ) : null}
          {eb.verified ? (
            <Tooltip
              title={format(new Date(eb.verified.updated_at), 'yyyy-MM-dd HH:mm:ss')}
              placement="top"
            >
              <DoneIcon sx={{ fontSize: 18, color: '#66BBA9' }} />
            </Tooltip>
          ) : null}
        </TableCell>

        <TableCell align="right">
          <ExtractionBatchActions
            eb={eb}
            setLoadingSendExtractionBatch={setLoadingSendExtractionBatch}
          />
        </TableCell>
      </TableRow>
      <ExtractionBatchRowGroup eb={eb} openRow={openRow} />
    </>
  );
};

export default ExtractionBatchRow;
